/**
 * OaSIS Explorer Frontend - URL State Management Utilities
 * Task 9.1: Advanced Search URL State Management
 * Task 9.4: Measurement Field Shorthand System
 * 
 * Utilities for encoding/decoding application state in URL query parameters
 * Enables shareable and bookmarkable search URLs
 * Uses shorthand field names to reduce URL length
 */

import { getFieldShorthand, getFieldFullName } from './fieldShorthandMapping';
import { toCanonicalGuideCode } from './guideCodes';

/**
 * Measurement filter structure matching useAdvancedSearch
 */
export interface MeasurementFilter {
  field: string;
  min?: number;
  max?: number;
  exact?: number | string;
}

/**
 * Encode measurement filters to compact URL format
 * 
 * Format: field=value, field>=min, field<=max, field=min-max
 * Multiple filters separated by ampersands
 * Uses shorthand field names to minimize URL length
 * 
 * @example
 * ```typescript
 * const filters = { leadership: { min: 4 }, teamwork: { exact: 5 } };
 * encodeFilters(filters); // "ldr>=4&tw=5"
 * ```
 * 
 * @param measurements - Record of field names to filter values
 * @returns Compact URL filter string
 */
export const encodeFilters = (measurements: Record<string, { min?: number; max?: number; exact?: number | string }>): string => {
  const filters: string[] = [];

  Object.entries(measurements).forEach(([field, value]) => {
    // Skip empty filters
    if (value.exact === undefined && value.min === undefined && value.max === undefined) {
      return;
    }

    // Use shorthand code for field name if available, fallback to full name
    const fieldName = getFieldShorthand(field) || field;

    // Exact match
    if (value.exact !== undefined) {
      filters.push(`${fieldName}=${value.exact}`);
      return;
    }

    // Range (both min and max)
    if (value.min !== undefined && value.max !== undefined) {
      filters.push(`${fieldName}=${value.min}-${value.max}`);
      return;
    }

    // Min only (greater than or equal)
    if (value.min !== undefined) {
      filters.push(`${fieldName}>=${value.min}`);
    }

    // Max only (less than or equal)
    if (value.max !== undefined) {
      filters.push(`${fieldName}<=${value.max}`);
    }
  });

  return filters.join('&');
};

/**
 * Decode compact URL filter string to measurement filters object
 * 
 * Gracefully handles invalid filters by ignoring them
 * Supports both old format (field:operator:value) and new compact format (field=value, field>=min, etc.)
 * 
 * @example
 * ```typescript
 * decodeFilters("ldr>=4&tw=5");
 * // { leadership: { min: 4 }, teamwork: { exact: 5 } }
 * ```
 * 
 * @param filterString - URL filter string (compact or legacy format)
 * @returns Record of field names to filter values
 */
export const decodeFilters = (filterString: string): Record<string, { min?: number; max?: number; exact?: number | string }> => {
  if (!filterString || !filterString.trim()) {
    return {};
  }

  const measurements: Record<string, { min?: number; max?: number; exact?: number | string }> = {};

  // Check if it's the new compact format (contains =, >=, <=, or -)
  const isCompactFormat = filterString.includes('=') || filterString.includes('>=') || filterString.includes('<=') || filterString.includes('-');

  if (isCompactFormat) {
    // Parse compact format: field=value, field>=min, field<=max, field=min-max
    const filterParts = filterString.split('&');

    filterParts.forEach(filterStr => {
      const trimmed = filterStr.trim();
      if (!trimmed) return;

      // Handle range format: field=min-max
      if (trimmed.includes('=') && trimmed.includes('-') && !trimmed.includes('>=') && !trimmed.includes('<=')) {
        const [fieldPart, valuePart] = trimmed.split('=');
        const field = fieldPart.trim();
        const [minStr, maxStr] = valuePart.split('-');
        
        if (!field || !minStr || !maxStr) return;

        const min = parseFloat(minStr.trim());
        const max = parseFloat(maxStr.trim());
        
        if (isNaN(min) || isNaN(max)) return;
        
        // Validate range: min cannot be greater than max
        if (min > max) return;

        const fullFieldName = getFieldFullName(field) || field;
        measurements[fullFieldName] = { min, max };
        return;
      }

      // Handle >= format: field>=min
      if (trimmed.includes('>=')) {
        const [fieldPart, valuePart] = trimmed.split('>=');
        const field = fieldPart.trim();
        const value = valuePart.trim();
        
        if (!field || !value) return;

        const numValue = parseFloat(value);
        if (isNaN(numValue)) return;

        const fullFieldName = getFieldFullName(field) || field;
        measurements[fullFieldName] = { min: numValue };
        return;
      }

      // Handle <= format: field<=max
      if (trimmed.includes('<=')) {
        const [fieldPart, valuePart] = trimmed.split('<=');
        const field = fieldPart.trim();
        const value = valuePart.trim();
        
        if (!field || !value) return;

        const numValue = parseFloat(value);
        if (isNaN(numValue)) return;

        const fullFieldName = getFieldFullName(field) || field;
        measurements[fullFieldName] = { max: numValue };
        return;
      }

      // Handle = format: field=value (exact match)
      if (trimmed.includes('=')) {
        const [fieldPart, valuePart] = trimmed.split('=');
        const field = fieldPart.trim();
        const value = valuePart.trim();
        
        if (!field || !value) return;

        // Try to parse as number, otherwise keep as string
        const numValue = parseFloat(value);
        const parsedValue = isNaN(numValue) ? value : numValue;

        const fullFieldName = getFieldFullName(field) || field;
        measurements[fullFieldName] = { exact: parsedValue };
        return;
      }
    });
  } else {
    // Legacy format: field:operator:value (semicolon-separated)
    const filterParts = filterString.split(';');

    filterParts.forEach(filterStr => {
      const parts = filterStr.split(':');
      
      // Need at least field:operator:value
      if (parts.length < 3) {
        return; // Skip invalid filter
      }

      const [field, operator, ...values] = parts;

      // Basic field validation (non-empty)
      if (!field || !field.trim()) {
        return; // Skip invalid field
      }

      // Convert shorthand back to full name if it's a shorthand code
      const fullFieldName = getFieldFullName(field) || field;

      // Validate operator
      const validOperators = ['eq', 'gte', 'lte', 'gt', 'lt', 'range'];
      if (!validOperators.includes(operator)) {
        return; // Skip invalid operator
      }

      // Handle range operator (requires min:max)
      if (operator === 'range') {
        if (values.length !== 2) {
          return; // Skip invalid range
        }

        const min = parseFloat(values[0]);
        const max = parseFloat(values[1]);

        if (isNaN(min) || isNaN(max)) {
          return; // Skip invalid numbers
        }
        
        // Validate range: min cannot be greater than max
        if (min > max) {
          return; // Skip invalid range
        }

        measurements[fullFieldName] = { min, max };
        return;
      }

      // Handle single value operators
      if (values.length === 0) {
        return; // Skip missing value
      }

      const value = values[0];

      // Try to parse as number, otherwise keep as string
      const numValue = parseFloat(value);
      const parsedValue = isNaN(numValue) ? value : numValue;

      // Map operators to filter structure
      switch (operator) {
        case 'eq':
          measurements[fullFieldName] = { exact: parsedValue };
          break;
        case 'gte':
        case 'gt':
          measurements[fullFieldName] = { min: typeof parsedValue === 'number' ? parsedValue : undefined };
          break;
        case 'lte':
        case 'lt':
          measurements[fullFieldName] = { max: typeof parsedValue === 'number' ? parsedValue : undefined };
          break;
      }
    });
  }

  return measurements;
};

/**
 * Encode search query for URL (simple passthrough with trimming)
 * 
 * @param query - Search query string
 * @returns Trimmed query string
 */
export const encodeSearchQuery = (query: string): string => {
  return query.trim();
};

/**
 * Decode search query from URL
 * 
 * @param query - URL query parameter value
 * @returns Decoded search query string
 */
export const decodeSearchQuery = (query: string | null): string => {
  return query?.trim() || '';
};


/**
 * Encode hierarchy prefixes for URL (compact format)
 * 
 * @param prefixes - Array of hierarchy prefix strings
 * @returns Object with individual hierarchy parameters
 */
export const encodeHierarchyPrefixes = (prefixes: string[]): Record<string, string> => {
  const result: Record<string, string> = {};
  prefixes.filter(prefix => prefix.trim()).forEach(prefix => {
    result[`h${prefix}`] = '1'; // Mark as selected
  });
  return result;
};

/**
 * Decode hierarchy prefixes from URL (compact format)
 * 
 * @param searchParams - URLSearchParams object to read from
 * @returns Array of hierarchy prefix strings
 */
export const decodeHierarchyPrefixes = (searchParams: URLSearchParams): string[] => {
  const prefixes: string[] = [];
  
  // Look for h0, h1, h2, etc. parameters
  searchParams.forEach((value, key) => {
    if (key.startsWith('h') && key.length > 1) {
      const prefix = key.substring(1); // Remove 'h' prefix
      if (value === '1' && prefix.match(/^\d+$/)) { // Only numeric prefixes
        prefixes.push(prefix);
      }
    }
  });
  
  return prefixes.sort((a, b) => parseInt(a) - parseInt(b));
};



/**
 * Encode taxonomy expanded nodes for URL (compact format)
 * 
 * @param expandedNodes - Array of expanded taxonomy node codes
 * @returns Object with individual expanded node parameters
 */
export const encodeExpandedNodes = (expandedNodes: string[]): Record<string, string> => {
  const result: Record<string, string> = {};
  expandedNodes.filter(node => node.trim()).forEach(node => {
    result[`e_${node}`] = '1'; // Mark as expanded (shorthand: e_)
  });
  return result;
};

/**
 * Decode taxonomy expanded nodes from URL (compact format)
 * 
 * Supports both new format (e_) and legacy format (exp_) for backward compatibility
 * 
 * @param searchParams - URLSearchParams object to read from
 * @returns Array of expanded taxonomy node codes
 */
export const decodeExpandedNodes = (searchParams: URLSearchParams): string[] => {
  const nodes: string[] = [];
  
  // Look for e_0, e_1, etc. parameters (new format)
  searchParams.forEach((value, key) => {
    if (key.startsWith('e_') && key.length > 2) {
      const node = key.substring(2); // Remove 'e_' prefix
      if (value === '1' && node.trim()) {
        nodes.push(node);
      }
    }
    // Backward compatibility: also check for legacy exp_ format
    else if (key.startsWith('exp_') && key.length > 4) {
      const node = key.substring(4); // Remove 'exp_' prefix
      if (value === '1' && node.trim()) {
        nodes.push(node);
      }
    }
  });
  
  return nodes.sort();
};


/**
 * Encode taxonomy selected categories for URL (compact format)
 * 
 * @param selectedCategories - Array of selected taxonomy category codes
 * @returns Object with individual selected category parameters
 */
export const encodeSelectedCategories = (selectedCategories: string[]): Record<string, string> => {
  const result: Record<string, string> = {};
  selectedCategories.filter(category => category.trim()).forEach(category => {
    result[`sel_${category}`] = '1'; // Mark as selected (shorthand: sel_)
  });
  return result;
};

/**
 * Decode taxonomy selected categories from URL (compact format)
 * 
 * @param searchParams - URLSearchParams object to read from
 * @returns Array of selected taxonomy category codes
 */
export const decodeSelectedCategories = (searchParams: URLSearchParams): string[] => {
  const categories: string[] = [];
  
  // Look for sel_0, sel_1, etc. parameters
  searchParams.forEach((value, key) => {
    if (key.startsWith('sel_') && key.length > 4) {
      const category = key.substring(4); // Remove 'sel_' prefix
      if (value === '1' && category.trim()) {
        categories.push(category);
      }
    }
  });
  
  return categories.sort();
};


/**
 * Encode taxonomy search query for URL
 * 
 * @param searchQuery - Search query text
 * @returns URL-encoded search query string
 */
export const encodeTaxonomySearch = (searchQuery: string): string => {
  return searchQuery.trim();
};

/**
 * Decode taxonomy search query from URL
 * 
 * @param searchParam - URL search parameter value
 * @returns Decoded search query string
 */
export const decodeTaxonomySearch = (searchParam: string | null): string => {
  return searchParam?.trim() || '';
};

/**
 * Encode measurements expanded nodes for URL (compact format)
 * 
 * @param expandedNodes - Array of expanded Guide node codes
 * @returns Object with individual expanded node parameters
 */
export const encodeMeasurementsExpandedNodes = (expandedNodes: string[]): Record<string, string> => {
  const result: Record<string, string> = {};
  const canonicalNodes = [...new Set(expandedNodes
    .map((node) => toCanonicalGuideCode(node))
    .filter((node) => node.trim()))];

  canonicalNodes.forEach((node) => {
    result[`mexp_${node}`] = '1'; // Mark as expanded (shorthand: mexp_)
  });
  return result;
};

/**
 * Decode measurements expanded nodes from URL (compact format)
 * 
 * @param searchParams - URLSearchParams object to read from
 * @returns Array of expanded Guide node codes
 */
export const decodeMeasurementsExpandedNodes = (searchParams: URLSearchParams): string[] => {
  const nodes: string[] = [];
  
  // Look for mexp_0, mexp_1, etc. parameters
  searchParams.forEach((value, key) => {
    if (key.startsWith('mexp_') && key.length > 5) {
      const node = key.substring(5); // Remove 'mexp_' prefix
      if (value === '1' && node.trim()) {
        nodes.push(toCanonicalGuideCode(node));
      }
    }
  });
  
  return [...new Set(nodes.filter(Boolean))].sort();
};


/**
 * Encode measurements selected categories for URL (compact format)
 * 
 * @param selectedCategories - Array of selected Guide category codes
 * @returns Object with individual selected category parameters
 */
export const encodeMeasurementsSelectedCategories = (selectedCategories: string[]): Record<string, string> => {
  const result: Record<string, string> = {};
  const canonicalCategories = [...new Set(selectedCategories
    .map((category) => toCanonicalGuideCode(category))
    .filter((category) => category.trim()))];

  canonicalCategories.forEach((category) => {
    result[`msel_${category}`] = '1'; // Mark as selected (shorthand: msel_)
  });
  return result;
};

/**
 * Decode measurements selected categories from URL (compact format)
 * 
 * @param searchParams - URLSearchParams object to read from
 * @returns Array of selected Guide category codes
 */
export const decodeMeasurementsSelectedCategories = (searchParams: URLSearchParams): string[] => {
  const categories: string[] = [];
  
  // Look for msel_0, msel_1, etc. parameters
  searchParams.forEach((value, key) => {
    if (key.startsWith('msel_') && key.length > 5) {
      const category = key.substring(5); // Remove 'msel_' prefix
      if (value === '1' && category.trim()) {
        categories.push(toCanonicalGuideCode(category));
      }
    }
  });
  
  return [...new Set(categories.filter(Boolean))].sort();
};


/**
 * Encode measurements search query for URL
 * 
 * @param searchQuery - Search query text
 * @returns URL-encoded search query string
 */
export const encodeMeasurementsSearch = (searchQuery: string): string => {
  return searchQuery.trim();
};

/**
 * Decode measurements search query from URL
 * 
 * @param searchParam - URL search parameter value
 * @returns Decoded search query string
 */
export const decodeMeasurementsSearch = (searchParam: string | null): string => {
  return searchParam?.trim() || '';
};

/**
 * Encode measurements selected descriptor for URL
 * 
 * @param selectedDescriptor - Selected descriptor code
 * @returns URL-encoded descriptor code string
 */
export const encodeMeasurementsSelectedDescriptor = (selectedDescriptor: string): string => {
  return toCanonicalGuideCode(selectedDescriptor);
};

/**
 * Decode measurements selected descriptor from URL
 * 
 * @param descriptorParam - URL descriptor parameter value
 * @returns Decoded descriptor code string
 */
export const decodeMeasurementsSelectedDescriptor = (descriptorParam: string | null): string => {
  return descriptorParam ? toCanonicalGuideCode(descriptorParam) : '';
};

/**
 * Encode TEER levels for URL (compact format)
 * 
 * @param levels - Array of TEER levels (0-5)
 * @returns Object with individual TEER parameters (t0=1, t1=1, etc.)
 */
export const encodeTeerLevels = (levels: number[]): Record<string, string> => {
  const result: Record<string, string> = {};
  levels.filter(level => level >= 0 && level <= 5).forEach(level => {
    result[`t${level}`] = '1'; // Mark as selected
  });
  return result;
};

/**
 * Decode TEER levels from URL (compact format)
 * 
 * @param searchParams - URLSearchParams object to read from
 * @returns Array of TEER levels (0-5)
 */
export const decodeTeerLevels = (searchParams: URLSearchParams): number[] => {
  const levels: number[] = [];
  
  // Look for t0, t1, t2, etc. parameters
  searchParams.forEach((value, key) => {
    if (key.startsWith('t') && key.length === 2) {
      const level = parseInt(key.substring(1)); // Remove 't' prefix
      if (value === '1' && level >= 0 && level <= 5) {
        levels.push(level);
      }
    }
  });
  
  return levels.sort((a, b) => a - b);
};

/**
 * Get page referrer from URL
 * Returns the value of the 'pref' parameter
 * 
 * @param searchParams - URLSearchParams object
 * @returns Page referrer value ('results', 'search', etc.) or null
 * 
 * @example
 * ```typescript
 * const referrer = getPageReferrer(searchParams);
 * if (referrer === 'results') {
 *   // Hide hero section
 * }
 * ```
 */
export const getPageReferrer = (searchParams: URLSearchParams): string | null => {
  return searchParams.get('pref');
};

/**
 * Set page referrer in URL
 * Adds or updates the 'pref' parameter
 * 
 * @param searchParams - URLSearchParams object
 * @param referrer - Page referrer value ('results', 'search', etc.) or null to remove
 * @returns New URLSearchParams with updated referrer
 * 
 * @example
 * ```typescript
 * const newParams = setPageReferrer(searchParams, 'results');
 * navigate(`/search?${newParams.toString()}`);
 * ```
 */
export const setPageReferrer = (
  searchParams: URLSearchParams, 
  referrer: 'results' | 'search' | null
): URLSearchParams => {
  const newParams = new URLSearchParams(searchParams);
  
  if (referrer) {
    newParams.set('pref', referrer);
  } else {
    newParams.delete('pref');
  }
  
  return newParams;
};




